package com.nonopn.skywatcher.design.widgets

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material.AlertDialog
import androidx.compose.material.ButtonDefaults
import androidx.compose.material.OutlinedButton
import androidx.compose.material.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import com.nonopn.skywatcher.design.local.LocalFontSizes
import com.nonopn.skywatcher.design.res.Res
import com.nonopn.skywatcher.design.res.cancel
import com.nonopn.skywatcher.design.res.confirm
import com.nonopn.skywatcher.design.theme.AppSizes
import com.nonopn.skywatcher.design.utils.localized
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.compose.widgets.TextTitle

@Suppress("LongMethod")
@Composable
fun PopupConfirm(
    shownUuid: String?,
    title: String,
    text: String,
    showCancel: Boolean = false,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit
) {
    var showedUuid: String? by remember { mutableStateOf(null) }
    var actualText by remember { mutableStateOf(text) }
    var actualTitle by remember { mutableStateOf(title) }

    LaunchedEffect(text) { actualText = text }
    LaunchedEffect(title) { actualTitle = title }

    LaunchedEffect(shownUuid) {
        if (shownUuid != showedUuid) {
            showedUuid = shownUuid
        }
    }

    if (null != showedUuid) {
        AlertDialog(
            title = {
                TextTitle(
                    color = Color.Black,
                    text = actualTitle
                )
            },
            text = {
                TextNormal(
                    color = Color.Black,
                    text = actualText
                )
            },
            onDismissRequest = {
                showedUuid = null
                onDismiss()
            },
            dismissButton = if (showCancel) {
                {
                    DisplayTextButton(
                        Res.string.cancel.localized(),
                        onClick = {
                            showedUuid = null
                            onDismiss()
                        }
                    )
                }
            } else {
                null
            },
            confirmButton = {
                DisplayOutlinedButton(
                    Res.string.confirm.localized(),
                    onClick = {
                        showedUuid = null
                        onConfirm()
                    }
                )
            }
        )
    }
}

@Suppress("LongMethod")
@Composable
fun PopupConfirm(
    shownUuid: String?,
    title: String,
    text: String,
    alternateText: (@Composable () -> Unit)? = null,
    onDismiss: () -> Unit,
    actions: List<PopupConfirmAction>
) = PopupConfirmWithButtonRows(shownUuid, title, text, alternateText, onDismiss, listOf(actions))

@Suppress("LongMethod")
@Composable
fun PopupConfirmWithButtonRows(
    shownUuid: String?,
    title: String,
    text: String,
    alternateText: (@Composable () -> Unit)? = null,
    onDismiss: () -> Unit,
    rowsOfActions: List<List<PopupConfirmAction>>
) {
    var showedUuid: String? by remember { mutableStateOf(null) }
    var actualText by remember { mutableStateOf(text) }
    var actualTitle by remember { mutableStateOf(title) }

    LaunchedEffect(text) { actualText = text }
    LaunchedEffect(title) { actualTitle = title }


    LaunchedEffect(shownUuid) {
        if (shownUuid != showedUuid) {
            showedUuid = shownUuid
        }
    }

    if (null == showedUuid) return

    AlertDialog(
        title = {
            TextNormal(
                color = Color.Black,
                fontSize = LocalFontSizes.current.popup.title,
                fontWeight = FontWeight.Bold,
                text = actualTitle
            )
        },
        text = {
            Column {
                TextNormal(
                    color = Color.Black,
                    fontSize = LocalFontSizes.current.popup.text,
                    text = actualText
                )

                alternateText?.let {
                    Spacer(Modifier.height(AppSizes.paddings.reduced))

                    it()
                }
            }
        },
        onDismissRequest = {
            showedUuid = null
            onDismiss()
        },
        buttons = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(AppSizes.paddings.reduced)
            ) {
                rowsOfActions.forEach { actions ->
                    Row(
                        modifier = Modifier.fillMaxWidth()
                            .padding(
                                horizontal = AppSizes.paddings.reduced
                            ),
                        horizontalArrangement = Arrangement.spacedBy(
                            AppSizes.paddings.reduced,
                            Alignment.End
                        )
                    ) {
                        actions.map { (text, isOutlined, textColor, backgroundColor, onClick) ->
                            if (isOutlined) {
                                DisplayOutlinedButton(text, textColor, backgroundColor) {
                                    showedUuid = null
                                    onClick()
                                }
                            } else {
                                DisplayTextButton(text, textColor, backgroundColor) {
                                    showedUuid = null
                                    onClick()
                                }
                            }
                        }
                    }
                }
            }
        }
    )
}

@Composable
private fun DisplayOutlinedButton(
    text: String,
    textColor: Color? = null,
    backgroundColor: Color? = null,
    onClick: () -> Unit
) {
    OutlinedButton(
        onClick = onClick,
        colors = backgroundColor?.let { containerColor ->
            ButtonDefaults.outlinedButtonColors(backgroundColor = containerColor)
        } ?: ButtonDefaults.outlinedButtonColors()
    ) {
        TextNormal(
            color = textColor ?: Color.Black,
            fontSize = LocalFontSizes.current.popup.button,
            text = text
        )
    }
}

@Composable
private fun DisplayTextButton(
    text: String,
    textColor: Color? = null,
    backgroundColor: Color? = null,
    onClick: () -> Unit
) {
    TextButton(
        onClick = onClick,
        colors = backgroundColor?.let { containerColor ->
            ButtonDefaults.textButtonColors(backgroundColor = containerColor)
        } ?: ButtonDefaults.textButtonColors()
    ) {
        TextNormal(
            color = textColor ?: Color.Black,
            fontSize = LocalFontSizes.current.popup.button,
            text = text
        )
    }
}

data class PopupConfirmAction(
    val text: String,
    val isOutlined: Boolean,
    val textColor: Color? = null,
    val backgroundColor: Color? = null,
    val onClick: () -> Unit
)
